/**
 * @file Loading screen component for Tablespoon Agency website
 * @description 
 * A fullscreen loading component that displays while the application initializes.
 * Shows a centered loading animation against a red background matching the brand color.
 * Ensures the loading screen is visible for at least the specified minimum time.
 * Only shows during initial page load, not during client-side navigation.
 * 
 * Key features:
 * - Fullscreen coverage using fixed positioning
 * - Centered loading GIF animation
 * - Brand-consistent styling with --red background
 * - Smooth fade-out animation when content is ready
 * - Guaranteed minimum display time
 * - Respects reduced motion preferences for accessibility
 * - Optimized animations for mobile devices
 * 
 * @dependencies
 * - motion, AnimatePresence: From Framer Motion for fade animations
 * - React useState/useEffect: For controlling loading state
 * - useReducedMotion: For respecting motion preferences
 */

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useReducedMotion } from "@/hooks/use-reduced-motion";
import { useIsMobile } from "@/hooks/use-mobile";

interface LoadingScreenProps {
  /** Minimum display time in milliseconds (default: 3000ms = 3 seconds) */
  minDisplayTime?: number;
  /** Function to call when loading is complete */
  onLoadingComplete?: () => void;
}

export function LoadingScreen({ 
  minDisplayTime = 3000,
  onLoadingComplete 
}: LoadingScreenProps) {
  const [isVisible, setIsVisible] = useState(true);
  const prefersReducedMotion = useReducedMotion();
  const isMobile = useIsMobile();
  
  // Adjust animation duration based on device and motion preferences
  const animationDuration = prefersReducedMotion ? 0 : (isMobile ? 0.4 : 0.8);

  useEffect(() => {
    // Record the start time when component mounts
    const startTime = Date.now();
    
    // Handle all resources loaded event
    const handleLoaded = () => {
      // If user prefers reduced motion, show for minimal time
      const effectiveMinTime = prefersReducedMotion ? Math.min(minDisplayTime, 1000) : minDisplayTime;
      
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, effectiveMinTime - elapsedTime);
      
      // Ensure we show the loading screen for at least minDisplayTime
      // (or reduced time for users who prefer reduced motion)
      setTimeout(() => {
        setIsVisible(false);
        if (onLoadingComplete) {
          onLoadingComplete();
        }
      }, remainingTime);
    };

    // Listen for window load event to ensure all resources are loaded
    window.addEventListener('load', handleLoaded);
    
    // Also set a backup timer in case the load event doesn't fire
    const timer = setTimeout(() => {
      handleLoaded();
    }, minDisplayTime);

    // Clean up listeners on unmount
    return () => {
      window.removeEventListener('load', handleLoaded);
      clearTimeout(timer);
    };
  }, [minDisplayTime, onLoadingComplete, prefersReducedMotion]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div 
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: "var(--red)" }}
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ 
            duration: animationDuration,
            ease: "easeInOut"
          }}
          aria-live="polite"
          aria-label="Loading content, please wait"
        >
          <div className="flex flex-col items-center">
            {/* We keep the GIF as is since it's a simple loading animation */}
            {/* For users with reduced motion preference, we could add a static alternative */}
            {prefersReducedMotion ? (
              <div className="w-40 h-40 flex items-center justify-center text-[var(--ivory)] text-xl font-light">
                Loading...
              </div>
            ) : (
              <img 
                src="/loading.gif" 
                alt="Loading..." 
                className="w-40 h-40" 
              />
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default LoadingScreen;